import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnInit, signal, untracked, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    ChatChannelController,
    FeatureFlagController,
    FeedFilterTypeEnum,
    McpController,
    OrderController,
    OrderEntity,
    OrderStatusEnum,
    UserController,
} from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs';
import {
    shouldShowWhatsappIntro,
    WHATSAPP_BANNER_CODE,
} from 'src/app/domain/controllers/chat-channel.controller';
import { McpIntroComponent } from '../../../mcp/components/mcp-intro/mcp-intro.component';
import { MCP_BANNER_CODE, MCP_FEATURE_FLAG } from '../../../mcp/mcp-apps.catalog';
import { PaginatedEntity, PaginationParams } from 'src/app/domain/DTOs/paginate.entity';
import { IaChatLauncherBubbleComponent } from 'src/app/modules/ia-chat/components/ia-chat-launcher-bubble/ia-chat-launcher-bubble.component';
import { CreateWithIaWizardComponent } from '../../components/create-with-ia-wizard/create-with-ia-wizard.component';
import { ChannelLinkComponent } from 'src/app/modules/integrations/components/channel-link/channel-link.component';
import { RaSelectModel } from 'src/app/shared/components/ra-select/model/ra-select.model';
import { FeedListComponent } from './components/feed-list/feed-list.component';
import { OrderDetailsComponent } from './components/order-details/order-details.component';
import { OrderEmptyComponent } from './components/order-empty/order-empty.component';
import { OrderListComponent } from './components/order-list/order-list.component';
import { OrderWelcomeComponent } from './components/order-welcome/order-welcome.component';
import {
    clampListWidthRem,
    LIST_WIDTH_DEFAULT_REM,
    LIST_WIDTH_MAX_REM,
    LIST_WIDTH_MIN_REM,
    LIST_WIDTH_STORAGE_KEY,
    readStoredListWidthRem,
} from './list-width.util';
import { FeedMainService } from './services/feed-main.service';
import { OrderMainService } from './services/order-main.service';

@Component({
    selector: 'order',
    templateUrl: 'order.page.html',
    styleUrl: 'order.page.scss',
    imports: [
        CommonModule,
        FeedListComponent,
        OrderListComponent,
        OrderDetailsComponent,
        OrderWelcomeComponent,
        OrderEmptyComponent,
        IaChatLauncherBubbleComponent,
        CreateWithIaWizardComponent,
        ChannelLinkComponent,
        McpIntroComponent,
        TranslatePipe,
    ],
})
export class OrderPage implements OnInit {
    public searchControl = new FormControl('');
    public statusOptions: RaSelectModel;
    public status = signal<OrderStatusEnum>(OrderStatusEnum.PROGRESS);
    public loading = signal(false);
    public orders = signal<OrderEntity[]>([]);
    public paginate = signal<PaginatedEntity<OrderEntity> | null>(null);

    /** Bolinha do chat em modo AVULSO (menu "Novo documento → Usar o chat"). */

    /**
     * Apresentação da novidade do WhatsApp, aberta sozinha na primeira entrada.
     *
     * As TRÊS condições precisam valer: o status já foi consultado, o usuário
     * não vinculou, e ele não fechou o aviso antes. Enquanto o status é `null`
     * nada abre, senão o modal apareceria e sumiria quando a resposta chegasse.
     */
    public showWhatsappIntro = computed(() => {
        if (this.featureFlagController.isBlocked('wa_integration')) return false;

        return shouldShowWhatsappIntro(
            this._chatChannelController.whatsapp(),
            this._userController.currentUser()?.dismissedBanners
        );
    });

    /**
     * Apresentação da novidade dos apps de IA (MCP), aberta sozinha na primeira
     * entrada de quem ainda não conectou nenhum app e não fechou o aviso.
     *
     * Enquanto a lista de conexões não chegou (`null`), nada abre, senão o modal
     * apareceria e sumiria quando a resposta chegasse. E nunca junto com a
     * apresentação do WhatsApp: dois modais de novidade ao mesmo tempo é demais,
     * então esta espera a outra ser fechada.
     */
    public showMcpIntro = computed(() => {
        if (this.featureFlagController.isBlocked(MCP_FEATURE_FLAG)) return false;
        if (this.showWelcome() || this.showWhatsappIntro()) return false;

        const connections = this._mcpController.connections();
        if (connections === null || connections.length > 0) return false;

        return !this._userController.currentUser()?.dismissedBanners?.includes(MCP_BANNER_CODE);
    });

    private _iaBubble = viewChild<IaChatLauncherBubbleComponent>('iaBubble');

    // * [Injects]
    private _toastService = inject(RaToastService);
    private _translate = inject(TranslateService);
    public orderMainService = inject(OrderMainService);
    private _userController = inject(UserController);
    private _chatChannelController = inject(ChatChannelController);
    private _mcpController = inject(McpController);
    public feedMainService = inject(FeedMainService);
    public featureFlagController = inject(FeatureFlagController);
    private _orderController = inject(OrderController);

    /** Feed unificado liberado pela flag `unified_feed` (whitelist do beta). */
    public unifiedFeedEnabled = computed(
        () => !this.featureFlagController.isBlocked('unified_feed')
    );

    /**
     * Primeiro uso do feed unificado: conta sem NENHUM item (sem busca ativa e
     * na aba "Tudo") → troca os dois painéis vazios pelo canvas de boas-vindas
     * (order-welcome). `loadedOnce` evita o flash do welcome antes da 1ª busca;
     * busca/aba/grupo ativos mantêm o empty state normal ("busca sem resultados"
     * ou "grupo vazio" não é "conta vazia").
     */
    public showWelcome = computed(
        () =>
            this.unifiedFeedEnabled() &&
            this.feedMainService.loadedOnce() &&
            !this.feedMainService.loading() &&
            this.feedMainService.items().length === 0 &&
            this.feedMainService.filterType() === FeedFilterTypeEnum.ALL &&
            this.feedMainService.groupId() === null &&
            !this.feedMainService.lastTextSearch()
    );

    /** Modo avulso pedido de fora (welcome); o card "Criar com IA" abre sem destino. */
    public chatStandalone = signal<boolean>(false);
    /** Texto escrito na tela sem seleção, entregue ao chat como rascunho. */
    public chatInitialDraft = signal<string>('');

    /**
     * A tela sem seleção está no ar? Ela já tem o campo da IA no corpo, então a bolinha
     * flutuante seria um segundo convite para a mesma coisa, por cima do primeiro.
     */
    public showEmptySelection = computed(
        () => !this.showWelcome() && this.orderMainService.currentOrder() == null
    );

    /** Wizard "Criar com IA" aberto, e o texto que veio do campo da tela. */
    public iaWizardOpen = signal(false);
    public iaWizardPrompt = signal('');

    /** "Começar" da tela sem seleção: abre o wizard com o que já foi escrito no campo. */
    public openIaWizard(prompt: string): void {
        this.iaWizardPrompt.set(prompt);
        this.iaWizardOpen.set(true);
    }

    // * ── Divisor entre a lista e os detalhes ─────────────────────────────────

    /** Largura escolhida pelo usuário, lembrada entre sessões. */
    public listWidthRem = signal<number>(
        readStoredListWidthRem(OrderPage._storage(), OrderPage._remPx(), window.innerWidth)
    );
    /** `true` durante o arrasto: trava a seleção de texto e mantém o cursor. */
    public resizingList = signal(false);

    public readonly listWidthMin = LIST_WIDTH_MIN_REM;
    public readonly listWidthMax = LIST_WIDTH_MAX_REM;

    /** `null` quando o navegador bloqueia o storage (janela privada). */
    private static _storage(): Storage | null {
        try {
            return localStorage;
        } catch {
            return null;
        }
    }

    private static _remPx(): number {
        return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    }

    private static _clamp(rem: number): number {
        return clampListWidthRem(rem, OrderPage._remPx(), window.innerWidth);
    }

    /**
     * Arrasta o divisor. Usa pointer capture para o arrasto continuar mesmo quando o
     * ponteiro sai do handle (ele tem menos de 1 rem de largura, então isso é a regra).
     */
    public onResizeStart(event: PointerEvent): void {
        event.preventDefault();

        const handle = event.target as HTMLElement;
        const remPx = OrderPage._remPx();
        const startX = event.clientX;
        const startRem = this.listWidthRem();

        handle.setPointerCapture(event.pointerId);
        this.resizingList.set(true);

        const move = (moveEvent: PointerEvent) =>
            this.listWidthRem.set(
                OrderPage._clamp(startRem + (moveEvent.clientX - startX) / remPx)
            );

        const end = () => {
            handle.releasePointerCapture(event.pointerId);
            handle.removeEventListener('pointermove', move);
            handle.removeEventListener('pointerup', end);
            handle.removeEventListener('pointercancel', end);

            this.resizingList.set(false);
            this._persistListWidth();
        };

        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', end);
        handle.addEventListener('pointercancel', end);
    }

    /** Teclado: o divisor é um controle de verdade, não só uma área de arrasto. */
    public onResizeKey(event: KeyboardEvent): void {
        const step = event.shiftKey ? 3 : 1;

        if (event.key === 'ArrowLeft')
            this.listWidthRem.set(OrderPage._clamp(this.listWidthRem() - step));
        else if (event.key === 'ArrowRight')
            this.listWidthRem.set(OrderPage._clamp(this.listWidthRem() + step));
        else if (event.key === 'Home') this.listWidthRem.set(LIST_WIDTH_MIN_REM);
        else if (event.key === 'End') this.listWidthRem.set(OrderPage._clamp(LIST_WIDTH_MAX_REM));
        else return;

        event.preventDefault();
        this._persistListWidth();
    }

    /** Duplo clique volta ao padrão — o jeito de desfazer um arrasto infeliz. */
    public onResizeReset(): void {
        this.listWidthRem.set(LIST_WIDTH_DEFAULT_REM);
        this._persistListWidth();
    }

    private _persistListWidth(): void {
        try {
            localStorage.setItem(LIST_WIDTH_STORAGE_KEY, String(this.listWidthRem()));
        } catch {
            // Sem storage a largura vale só para esta sessão; nada a fazer.
        }
    }

    // O card "Criar com IA" do modal "+ Novo" e a tela de boas-vindas pedem a
    // abertura do chat — a bolinha vive aqui na page. O modo (avulso ou sem
    // destino) vem junto no serviço.
    // CONSOME e ZERA o tick: o serviço é root e sobrevive à navegação; sem o
    // reset, recriar a page (ex.: voltar do editor após remover um documento)
    // relia o tick antigo e reabria o chat sozinho.
    private _chatOpenEffect = effect(() => {
        if (this.feedMainService.chatOpenTick() === 0) return;
        this.feedMainService.chatOpenTick.set(0);
        this.chatStandalone.set(this.feedMainService.chatOpenStandalone());
        this._iaBubble()?.open();
    });

    /** "+" da lista (e "Novo documento" sem IA no plano): abre o wizard. Consome e zera. */
    private _iaWizardEffect = effect(() => {
        if (this.feedMainService.iaWizardTick() === 0) return;
        this.feedMainService.iaWizardTick.set(0);
        untracked(() => this.openIaWizard(''));
    });

    /** Pós-geração via chat: recarrega documentos do serviço atual e o feed. */
    public onChatConcluded(): void {
        this.orderMainService.requestDocumentsReload();
        if (this.unifiedFeedEnabled()) this.feedMainService.getFeed(undefined, 1).subscribe();
    }

    ngOnInit(): void {
        this._buildStatusOptions();
        this.getOrders();
        this._listenSearchControl();

        // Reabriu a page já no welcome (feed vazio de uma visita anterior): o
        // feed-list não monta, então ninguém revalidaria o feed — refresh
        // silencioso pra dispensar o welcome caso algo tenha sido criado em
        // outra aba/dispositivo.
        if (this.showWelcome()) this.feedMainService.refreshSilent();

        // A barra do topo já consulta o vínculo em toda tela, então aqui não
        // repetimos: o sinal do controller é o mesmo.

        // Conexões dos apps de IA: decidem se a apresentação da novidade abre.
        if (!this.featureFlagController.isBlocked(MCP_FEATURE_FLAG))
            this._mcpController.getConnections().subscribe({ error: () => undefined });
    }

    /** Fecha a apresentação dos apps de IA para sempre, tenha conectado ou não. */
    public closeMcpIntro(): void {
        this._userController.dismissBanner(MCP_BANNER_CODE).subscribe();
    }

    /**
     * Fecha a apresentação para sempre, tenha ele vinculado ou não.
     *
     * Marcar como visto no fechamento (e não só no "Agora não") é o que evita
     * o modal voltar a bloquear a home em toda visita de quem já viu a novidade.
     * Depois disso o caminho é Configurações.
     */
    public closeWhatsappIntro(): void {
        this._userController.dismissBanner(WHATSAPP_BANNER_CODE).subscribe();
    }

    private _buildStatusOptions(): void {
        this.statusOptions = new RaSelectModel();
        this.statusOptions.label = this._translate.instant('order.status.label');
        this.statusOptions.placeholder = this._translate.instant('order.status.placeholder');

        this.statusOptions.listOptions = [
            {
                value: OrderStatusEnum.PROGRESS,
                description: this._translate.instant('order.status.inProgress'),
                selected: true,
            },
            {
                value: OrderStatusEnum.FINISHED,
                description: this._translate.instant('order.status.archived'),
            },
        ];

        this.status.set(OrderStatusEnum.PROGRESS);
    }

    /**
     * Escuta as mudanças no input de busca
     */
    private _listenSearchControl(): void {
        this.searchControl.valueChanges
            .pipe(
                skip(1), // Ignora o valor inicial para evitar requisição duplicada no carregamento
                debounceTime(600),
                distinctUntilChanged()
            )
            .subscribe(() => this.getOrders());
    }

    /**
     * Busca os serviços
     */
    public getOrders(): void {
        this.loading.set(true);

        const paginate: PaginationParams = {
            page: this.paginate()?.page ?? 1,
            limit: this.paginate()?.limit ?? 9,
        };

        // Bloqueia os campos de filtro durante a busca
        this.statusOptions.disabled = true;
        this.searchControl.disable();

        // Busca os serviços
        this._orderController
            .getByFilter(this.status(), paginate, this.searchControl.value)
            .subscribe({
                next: paginate => {
                    this.paginate.set(paginate);
                    this.orders.set(paginate.data);

                    // Trava o input de busca caso não tenha serviços
                    this.statusOptions.disabled = false;
                    this.orders().length === 0
                        ? this.searchControl.disable()
                        : this.searchControl.enable();

                    this.loading.set(false);
                },
                error: error => {
                    console.error(error);
                    this.loading.set(false);
                    this._toastService.error(error.message);
                },
            });
    }

    /**
     * Muda a página para a página especificada
     * @param page Número da página
     */
    public changePageTo(page: number): void {
        this.paginate.set({
            ...this.paginate()!,
            page,
        });

        // Busca os serviços
        this.getOrders();
    }
}
